// src/hooks/useSocket.ts
import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { setOnlineUsers, setSyncStatus } from 'store/slices/uiSlice';
import { showToast } from 'store/slices/toastSlice';
import { setRoles } from 'store/slices/hierarchySlice';
import { addReviewRequest } from 'store/slices/reviewRequestSlice';
import { roleService } from 'services/roleService';
import { useQueryClient } from '@tanstack/react-query';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export function useSocket() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const { accessToken } = useSelector((s: RootState) => s.auth);
  const { user } = useSelector((s: RootState) => s.auth);

  useEffect(() => {
    if (!accessToken || !user) return;

    const socket = io(`${SOCKET_URL}/tasks`, {
      auth: { token: accessToken },
    });

    socketRef.current = socket;

    // Invalidate React Query cache when tasks change remotely
    const invalidateTasks = () => queryClient.invalidateQueries({ queryKey: ['tasks'] });

    socket.on('connect', () => {
      dispatch(setSyncStatus('live'));
      socket.emit('join:session');
      // Covers everything the socket couldn't tell us about while it wasn't
      // connected — first mount, a dropped connection, or the browser
      // throttling/suspending the socket while the tab was backgrounded.
      // Without this, a task assigned during that gap only ever shows up
      // after a manual page refresh, since no live event was ever received
      // for it and nothing else re-fetches on its own.
      invalidateTasks();
    });

    socket.on('disconnect', () => {
      dispatch(setSyncStatus('off'));
    });

    // A backgrounded tab can have its socket connection throttled/suspended
    // by the browser without ever firing 'disconnect' — catch that case too
    // by resyncing as soon as the tab becomes visible again.
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') invalidateTasks();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    socket.on('task:created', invalidateTasks);
    socket.on('task:updated', invalidateTasks);
    socket.on('task:deleted', invalidateTasks);
    socket.on('task:accepted', invalidateTasks);
    socket.on('task:progress', invalidateTasks);
    socket.on('task:submitted', invalidateTasks);
    socket.on('task:reviewed', invalidateTasks);

    // Discussion panel messages — the backend already emits these on every
    // post/pin/read/delete, but nothing here was listening, so the other
    // side of a conversation never saw a new message until they refreshed.
    const invalidateComments = ({ taskId }: { taskId: string }) =>
      queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'comments'] });
    socket.on('comment:created', invalidateComments);
    socket.on('comment:updated', invalidateComments);
    socket.on('comment:deleted', invalidateComments);

    // A real alert (not just a silent refresh) for the other side of a task
    // when the assignee/assigner does something meaningful to it.
    socket.on('task:notify', ({ message }: { taskId: string; message: string }) => {
      dispatch(showToast(message));
    });

    // Actionable — rendered as a persistent popup (ReviewRequestPopup) with an
    // inline Complete button, not just a toast, so the assigner doesn't have
    // to go find the task themselves to approve it.
    socket.on('task:review-requested', (payload: { taskId: string; title: string; fromName: string }) => {
      dispatch(addReviewRequest(payload));
    });

    socket.on('habit:updated', () => {
      queryClient.invalidateQueries({ queryKey: ['habits'] });
    });

    // Hierarchy/role changed by an admin — refresh the cached role list live
    socket.on('role:updated', () => {
      roleService.getAll().then((roles) => dispatch(setRoles(roles))).catch(() => {});
    });

    // Presence
    socket.on('presence:update', (users: Array<{ role: string }>) => {
      dispatch(setOnlineUsers(users));
    });

    // Keep-alive presence ping every 20s
    const pingInterval = setInterval(() => {
      socket.emit('ping:presence', { role: user.role });
    }, 20_000);

    return () => {
      clearInterval(pingInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      socket.disconnect();
    };
  }, [accessToken, user, dispatch, queryClient]);

  return socketRef;
}
